import httpClient from './httpClient'
import LocalStorage from '../utils/localStorage'
import type {
    LoginRequest,
    GoogleLoginRequest,
    RegisterRequest,
    RegisterResponse,
    AuthResponse,
    ForgotPasswordRequest,
    VerifyOtpRequest,
    ResetPasswordRequest,
    ChangePasswordRequest,
} from '../types/auth.types'
import type { UserProfile } from '../types/user.types'

// ============================================================
//  Auth Service
//  All auth-related API calls — replace endpoints to match
//  your backend API contract.
// ============================================================

const AUTH_PREFIX = 'user-auth/api/users'

const authService = {
    // ── Login ─────────────────────────────────────────────────
    login: async (payload: LoginRequest): Promise<AuthResponse> => {
        const response = await httpClient.post<AuthResponse>(`${AUTH_PREFIX}/login`, payload)
        
        console.log('[Auth Service] Login response:', response)
        console.log('[Auth Service] Response headers:', response.headers)
        console.log('[Auth Service] Response data:', response.data)
        
        // Access token from headers; refresh is HttpOnly cookie only
        const headerToken = response.headers['authorization']
        const headerUserId = response.headers['userid']
        
        if (response.data.tokens) {
            LocalStorage.setApiKey(response.data.tokens.accessToken)
            if (response.data.user?.id) {
                LocalStorage.setUserId(response.data.user.id)
            }
            if (response.data.user) {
                LocalStorage.setUserData(response.data.user)
            }
        }
        
        if (headerToken) {
            const cleanToken = headerToken.replace(/^Bearer\s+/i, '')
            LocalStorage.setApiKey(cleanToken)
        }
        if (headerUserId) {
            LocalStorage.setUserId(headerUserId)
        }
        
        return response.data
    },

    // ── Google Login ──────────────────────────────────────────
    loginWithGoogle: async (payload: GoogleLoginRequest): Promise<AuthResponse> => {
        const response = await httpClient.post<AuthResponse>('user-auth/api/login/google', payload)
        
        const token = response.headers['authorization']
        const userId = response.headers['userid']
        
        if (token && userId) {
            LocalStorage.setApiKey(token)
            LocalStorage.setUserId(userId)
        }
        
        return response.data
    },

    // ── Register ──────────────────────────────────────────────
    register: async (payload: RegisterRequest): Promise<RegisterResponse> => {
        const response = await httpClient.post<RegisterResponse>('user-auth/api/users', payload)
        
        const token = response.headers['authorization']
        const userId = response.headers['userid']
        
        if (token && userId) {
            LocalStorage.setApiKey(token)
            LocalStorage.setUserId(userId)
        }
        
        return response.data
    },

    // ── Logout ────────────────────────────────────────────────
    logout: async (): Promise<void> => {
        try {
            await httpClient.post(`${AUTH_PREFIX}/logout`)
        } finally {
            LocalStorage.clearAuth()
        }
    },

    // ── Forgot Password: send OTP ─────────────────────────────
    sendPasswordResetEmail: async (payload: ForgotPasswordRequest): Promise<{ message: string }> => {
        const { data } = await httpClient.post(`${AUTH_PREFIX}/forgot-password`, payload)
        return data
    },

    // ── Verify OTP ────────────────────────────────────────────
    verifyOtp: async (payload: VerifyOtpRequest): Promise<{ valid: boolean; message: string }> => {
        const { data } = await httpClient.post(`${AUTH_PREFIX}/verify-otp`, payload)
        return data
    },

    // ── Reset Password ────────────────────────────────────────
    resetPassword: async (payload: ResetPasswordRequest): Promise<{ message: string }> => {
        const { data } = await httpClient.post(`${AUTH_PREFIX}/reset-password`, payload)
        return data
    },

    // ── Change Password: authenticated user ──────────────────
    changePassword: async (payload: ChangePasswordRequest): Promise<{ message: string }> => {
        const { data } = await httpClient.post(`${AUTH_PREFIX}/change-password`, payload)
        return data
    },

    // ── Get current user profile ──────────────────────────────
    getMe: async () => {
        const { data } = await httpClient.get(`${AUTH_PREFIX}/me`)
        return data
    },

    // ── Get user by ID ────────────────────────────────────────
    getUserById: async (userId: string): Promise<UserProfile> => {
        const { data } = await httpClient.get<UserProfile>(`${AUTH_PREFIX}/${userId}`)
        return data
    },

    // ── Helper: check if user has a valid token ───────────────
    isAuthenticated: (): boolean => {
        return LocalStorage.isAuthenticated()
    },
}

export default authService
